/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

/**
 * Global Loader Service - Event-Based
 * Gestisce il loader globale tramite eventi DOM personalizzati
 * Non richiede Context Provider, funziona completamente disaccoppiato
 */

/**
 * Configuration options for the global loader
 */
export interface IGlobalLoaderConfig {
    /**
     * Optional message to display with the loader
     */
    message?: string;

    /**
     * Whether to show an overlay backdrop
     * @default true
     */
    showOverlay?: boolean;

    /**
     * Custom CSS class for styling
     */
    className?: string;

    /**
     * Minimum display time in milliseconds to prevent flickering
     * @default 300
     */
    minDisplayTime?: number;
}

/**
 * Event detail for loader events
 */
export interface ILoaderEventDetail {
    config?: IGlobalLoaderConfig;
    message?: string;
}

/**
 * Custom event names
 */
export const LOADER_EVENTS = {
    SHOW: 'globalloader:show',
    HIDE: 'globalloader:hide',
    UPDATE: 'globalloader:update'
} as const;

/**
 * Global Loader Service
 * Provides methods to control the global loader via DOM events
 */
const LoaderService = {
    counter: 0,
    showTime: 0,
    hideTimeout: null as NodeJS.Timeout | null,
    currentConfig: {
        showOverlay: true,
        minDisplayTime: 300
    } as IGlobalLoaderConfig,

    show(config?: string | IGlobalLoaderConfig): void {
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = null;
        }

        this.counter++;

        let loaderConfig: IGlobalLoaderConfig = { ...this.currentConfig };
        if (typeof config === 'string') {
            loaderConfig.message = config;
        } else if (config) {
            loaderConfig = { ...loaderConfig, ...config };
        }

        this.currentConfig = loaderConfig;

        if (this.counter === 1) {
            this.showTime = Date.now();
        }

        this.dispatchEvent(LOADER_EVENTS.SHOW, {
            config: loaderConfig,
            message: loaderConfig.message
        });

        if (typeof window !== 'undefined' && (window as any).__DEV__) {
            console.log('[LoaderService] Show called, counter:', this.counter);
        }
    },

    hide(force = false): void {
        this.counter = Math.max(0, this.counter - 1);

        if (typeof window !== 'undefined' && (window as any).__DEV__) {
            console.log('[LoaderService] Hide called, counter:', this.counter);
        }

        if (this.counter === 0 || force) {
            const elapsed = Date.now() - this.showTime;
            const minDisplayTime = this.currentConfig.minDisplayTime || 300;

            if (elapsed < minDisplayTime) {
                this.hideTimeout = setTimeout(() => {
                    this.dispatchEvent(LOADER_EVENTS.HIDE, {});
                    this.currentConfig = { ...this.currentConfig, message: undefined };
                }, minDisplayTime - elapsed);
            } else {
                this.dispatchEvent(LOADER_EVENTS.HIDE, {});
                this.currentConfig = { ...this.currentConfig, message: undefined };
            }
        }
    },

    updateMessage(message: string): void {
        this.currentConfig = { ...this.currentConfig, message };
        this.dispatchEvent(LOADER_EVENTS.UPDATE, { message });
    },

    getCounter(): number {
        return this.counter;
    },

    reset(): void {
        this.counter = 0;
        this.showTime = 0;
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = null;
        }
        this.currentConfig = {
            showOverlay: true,
            minDisplayTime: 300
        };
    },

    dispatchEvent(eventName: string, detail: ILoaderEventDetail): void {
        if (typeof window === 'undefined') {
            return;
        }

        const event = new CustomEvent(eventName, {
            detail,
            bubbles: true,
            cancelable: false
        });

        window.dispatchEvent(event);
    },

    addEventListener(eventName: string, handler: (event: CustomEvent<ILoaderEventDetail>) => void): () => void {
        if (typeof window === 'undefined') {
            return () => {};
        }

        window.addEventListener(eventName, handler as EventListener);

        return () => {
            window.removeEventListener(eventName, handler as EventListener);
        };
    }
};

export default LoaderService;
